import {
  $createTableNode,
  $createTableRowNode,
  $createTableCellNode,
  $isTableNode,
  $isTableRowNode,
  $isTableCellNode,
  TableNode,
  TableRowNode,
  TableCellNode
} from "@lexical/table";
import { $createParagraphNode, $createTextNode, $isParagraphNode } from "lexical";

/**
 * Markdown table transformer following the official Lexical guide pattern
 * Converts markdown tables to Lexical TableNodes and vice versa
 */
export const TABLE = {
  dependencies: [TableNode, TableRowNode, TableCellNode],
  export: (node) => {
    if (!$isTableNode(node)) {
      return null;
    }

    const output = [];
    const rows = node.getChildren();
    
    rows.forEach((row, rowIndex) => {
      if ($isTableRowNode(row)) {
        const cells = row.getChildren();
        const rowData = [];
        
        cells.forEach((cell) => {
          if ($isTableCellNode(cell)) {
            // Get cell content as plain text
            let cellContent = '';
            const cellChildren = cell.getChildren();
            cellChildren.forEach((child) => {
              if ($isParagraphNode(child)) {
                cellContent += child.getTextContent();
              } else {
                cellContent += child.getTextContent();
              }
            });
            rowData.push(cellContent.trim() || ' ');
          }
        });
        
        if (rowData.length > 0) {
          output.push(`| ${rowData.join(' | ')} |`);
          
          // Add separator row after header (first row)
          if (rowIndex === 0) {
            const separator = rowData.map(() => '---').join(' | ');
            output.push(`| ${separator} |`);
          }
        }
      }
    });

    return output.length > 0 ? output.join('\n') : null;
  },
  regExp: /^\|(.+)\|$/,
  replace: (parentNode, _1, _2, isImport) => {
    // This function handles the conversion from markdown to table nodes
    // We'll implement a more sophisticated parser in the transform function
    return;
  },
  type: "element"
};

/**
 * Parse markdown table string into table data structure
 */
export function parseMarkdownTable(text) {
  const lines = text.split('\n').map(line => line.trim()).filter(line => line);
  
  if (lines.length < 2) {
    return { rows: [], isValid: false };
  }

  // Check if it looks like a table (starts and ends with |)
  const tableLines = lines.filter(line => line.startsWith('|') && line.endsWith('|'));
  
  if (tableLines.length < 2) {
    return { rows: [], isValid: false };
  }

  // Parse header row
  const headerCells = tableLines[0]
    .slice(1, -1) // Remove leading and trailing |
    .split('|')
    .map(cell => cell.trim());

  // Check if second line is separator (contains ---)
  const separatorLine = tableLines[1];
  const isSeparator = separatorLine.includes('---') || separatorLine.includes(':--') || separatorLine.includes('--:');
  
  if (!isSeparator) {
    return { rows: [], isValid: false };
  }

  const rows = [headerCells];

  // Parse data rows (skip separator line)
  for (let i = 2; i < tableLines.length; i++) {
    const cells = tableLines[i]
      .slice(1, -1) // Remove leading and trailing |
      .split('|')
      .map(cell => cell.trim());
    
    // Ensure all rows have the same number of columns
    while (cells.length < headerCells.length) {
      cells.push('');
    }
    
    rows.push(cells.slice(0, headerCells.length));
  }

  return { rows, isValid: true };
}

/**
 * Create Lexical table node from parsed table data
 */
export function $createTableFromData(tableData) {
  const tableNode = $createTableNode();
  
  tableData.forEach((rowData, rowIndex) => {
    const tableRow = $createTableRowNode();
    
    rowData.forEach((cellData) => {
      const tableCell = $createTableCellNode(rowIndex === 0 ? 1 : 0); // Header type for first row
      const paragraph = $createParagraphNode();
      const textNode = $createTextNode(cellData);
      
      paragraph.append(textNode);
      tableCell.append(paragraph);
      tableRow.append(tableCell);
    });
    
    tableNode.append(tableRow);
  });

  return tableNode;
}
